import React, { Dispatch, SetStateAction } from 'react';

import {
  AttendeeFragment,
  ChatType,
  Maybe,
  PublicEWebinarFragment,
  PublicSetFragment,
  PublicSetWithEWebinarFragment,
  WatchedSegment,
} from '$/GraphQL/schema/schema';

import { default as Player } from '@vimeo/player';
import { createWithPartialContext } from '$/hooks/withPartialContexts';

import type { Rooms, WebinarRoom } from './Rooms/Rooms';
import type { InteractionInfo } from '../Interactions/InteractionInfo';
import type { InteractionFactory } from '../Interactions/InteractionFactory';
import type { HasJoined, PlayerMode, PlayerPlacement } from './types';
import type EDate from '$/shared/frontend/utils/EDate';
import type { AnimationControls, MotionValue } from 'framer-motion';
import { Transitions } from './transitions';
export { staticWebinarDimensions } from './staticWebinarDimensions';

export type WebinarEditMode =
  | 'edit'
  | 'edit.modal'
  | 'dash.analytics'
  | 'dash.chat'
  | 'dash.interactions';

interface WebinarPlayerColors {
  room: string;
  presentationRoom: string;
  text: string;
  controlBg: string;
  control: string;
  controlHover: string;
  sliderBg: string;
  sliderTrackBg: string;
  zoomSliderBg: string;
}

export type VideoBlockedType = 'forward' | 'backward' | 'all' | 'none';
export interface PlaySeekBlocked {
  type: VideoBlockedType;
  atPosition?: number; // Pause or Play @ this position
}

export interface HeightAndOffset {
  height: number;
  offset: number;
}
export interface TimelineComponents {
  title?: HeightAndOffset;
  conversions?: HeightAndOffset;
  imageOverlays?: HeightAndOffset;
  main?: HeightAndOffset;
  anchor: HeightAndOffset;
  time: HeightAndOffset;
  scroll: HeightAndOffset;
}

export interface ActionsPaneDimensions {
  location: 'right' | 'bottom' | 'none';
  whiteOutHeightRem: number;
  marginTopPx: number;
  heightPx: number;
  rightWidthRem: number; // Width of the action pane when Action Pane location is on the right
  bottomWidthRem: number; // Width of the action pane when Action Pane location is on the bottom
  maxWidthRem: number; // Largest the pane will get in this mode when on the right
  actionsTopPx: number;
  actionsXPx: number;
  snappedCardTopPx: number;

  pinnedCard: {
    height: number;
    scale: number;
    radius: number;
  };
  icard: {
    inactivityTimeoutSecs: number;
    mediaTitleRatio: number;
    mediaTitleHeightPx: number;
    titleHeightPx: number;

    paddingLeftRem: number;
    marginYpx: number;
    widthRem: number;
    modalPreviewWidth: number;
    compressed: {
      pageHeight: number;
      cardHeight: number;
      marginY: number;
      parallax: number;
    };
    expanded: {
      mediaTitleHeightPx: number;
      titleHeight: number;
      duration: number;
    };
  };
  idock: {
    top: number;
    iconsTop: number;
    widthRem: number;
    paddingLeftRem: number;
    paddingRightRem: number;
  };
}

export interface WebinarDimensions {
  isMeasured: boolean;
  windowArea: {
    width: number;
    height: number;
  };
  webinarArea: {
    width: number;
    height: number;
  };
  videoControlsArea: {
    portraitRatio: number;
    width: number;
    height: number;

    topBar: {
      height: number;
    };

    bottomBar: {
      height: number;
      paddingX: number;
      paddingY: number;
    };
  };
  roomArea: {
    top: number;
    width: number;
    height: number;
    aspectRatio: number;
  };
  videoArea: {
    top: number;
    width: number;
    height: number;
    aspectRatio: number;
  };
  videoStage: {
    radius: number;
    scale: number;
    width: number;
    height: number;
    unscaled: {
      width: number;
      height: number;
    };
  };
  video: {
    width: number;
    height: number;
    aspectRatio: number;
  };
  actionsPane: ActionsPaneDimensions;
  chat: {
    widthRem: number;
    heightPx: number;
    paddingLeftRem: number;
    paddingBottomRem: number;
  };
}

export type ActiveInteraction =
  | {
      lastActive: EDate;
      id: string;
    }
  | undefined;

export type ActionsPaneStates = 'offscreen' | 'hidden' | 'shown';
interface NextWebinarState {
  set: PublicSetWithEWebinarFragment | undefined | null;
  invalid: boolean;
}
export type InteractionState = NextWebinarState;
export type InteractionStateType = { [shortId: string]: InteractionState };

export interface WebinarPlayerState {
  editMode?: WebinarEditMode;
  dataLoaded: boolean;
  colors: WebinarPlayerColors;
  set: PublicSetFragment;
  ewebinar: PublicEWebinarFragment;
  duration: number;
  rooms: Rooms;
  currentRoom: WebinarRoom;
  domain: [number, number];

  interactions: InteractionInfo[];
  cardInteractions: InteractionInfo[];
  cardInteractionsRef: React.MutableRefObject<InteractionInfo[]>;
  nonCardInteractions: InteractionInfo[];
  overlaysUnderControls: InteractionInfo[];
  overlaysOverControls: InteractionInfo[];
  overlaysOverArea: InteractionInfo[];
  pinnedInteraction?: InteractionInfo;

  willBeRemovedCards: Record<string, boolean>;
  setWillBeRemovedCard: (interactionId: string, isSubmitting: boolean) => void;

  interactionHeights: { [shortId: string]: number };
  fastInteractionHeights: React.RefObject<{ [shortId: string]: number }>;
  interactionHeightsVersion: MotionValue<number>;

  setInteractionHeight: (shortId: string, height: number, updateSmallerHeight?: boolean) => void;
  activeInteractionId?: string;
  activeInteractionRef: React.RefObject<ActiveInteraction>;
  markInteractionActive: (interaction: InteractionInfo) => void;
  markInteractionInactive: (interaction?: InteractionInfo) => void;
  hasAgenda: boolean;
  setFocusedCardMode: Dispatch<SetStateAction<boolean>>;
  focusedCardMode: boolean;

  isEditingInteractions: boolean;
  interactionInEdit?: InteractionInfo;
  interactionInEditModalId?: string;
  setInteractionInEditModalId: (id?: string) => void;

  interactionFactory: InteractionFactory;
  onInteractionIconClick?: (id: string) => void;
  totalReactions: number;
  includeLegacyInteractions: boolean;
  setIncludeLegacyInteractions: Dispatch<SetStateAction<boolean>>;

  attendee?: AttendeeFragment;
  attendeeId?: string;
  chatType?: ChatType;
  startTime?: number;
  joinTime?: Date | undefined;
  hasJoined: HasJoined;
  showOverlay: boolean;
  setShowOverlay: (showOverlay: boolean) => void;
  setHasJoined: (joined: HasJoined) => void;
  clickedJoin: () => void;

  headerSize: number;
  timelineHeight: number;
  timelineComponents: TimelineComponents;
  loaded: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  errorCode?: number;
  joinMessage: string | null;
  isBuffering: boolean;
  isSeeking: boolean;
  seekingFromRef: React.RefObject<number>;
  isSeekingRef: React.RefObject<boolean>;
  playbackPosition: number;
  playbackPositionRef: React.RefObject<number>;
  playbackPositionValue: MotionValue<number>;
  lastSegmentRef: React.RefObject<Maybe<Omit<WatchedSegment, 'cid'>> | undefined>;
  setLastSegment: (segment: Omit<WatchedSegment, 'cid'>) => void;
  mode: PlayerMode;

  placement: PlayerPlacement;
  isEmbeddedPlayer: boolean;
  isCollapsedEmbeddedPlayer: boolean;

  player?: Player;
  vimeoVideoId?: number | null;
  vimeoHash?: string | null;
  vimeoDuration?: number;
  playerIframeRef: (node: HTMLIFrameElement) => void;
  webinarAreaRef: (node: HTMLDivElement) => void;
  dimensions: WebinarDimensions;
  dimensionsRef: React.MutableRefObject<WebinarDimensions>;
  transitions: Transitions;
  scrollToCardOrPositionTarget: InteractionInfo | number | 'snap' | null;
  scrollToCardOrPosition: (i: InteractionInfo | number | 'snap' | null) => void;
  scrollCardsToPosition: (pos: number) => void;

  pauseTimer: number;
  pauseTimerRemaining: number | null;
  pauseMessage?: string;
  isPlaying: boolean;
  setIsPlaying: (
    play: boolean,
    props?: {
      secsToPause?: number | null; // Start pause timer
      message?: string | null; // Message to show during pause
      atPosition?: number; // Pause or Play @ this position
      blockPlayAndSeek?: VideoBlockedType; // Don't allow seek or play across player until this is called again with noSeekOrPlay = false
    }
  ) => void;
  isScrobbling: boolean;
  scrobblingFromRef: React.MutableRefObject<number | null>;
  setScrobbling: (scrobbling: boolean, position: number) => void;
  setPauseTimer: (seconds: number) => void;
  setPlaybackPosition: (seconds: number) => void;
  muted: boolean;
  setMuted: Dispatch<SetStateAction<boolean>>;
  mutedByJoiningIssue: boolean;
  setMutedByJoiningIssue: Dispatch<SetStateAction<boolean>>;
  volume: number;
  setVolume: Dispatch<SetStateAction<number>>;
  playbackSpeed: number;
  setPlaybackSpeed: (speed: number) => void;
  stateModalClose: boolean;
  setStateModalClose: (val: boolean) => void;

  actionsPaneState: ActionsPaneStates;
  setActionsPaneState: (state: ActionsPaneStates) => void;

  isReplayExpired: boolean;
  setIsReplayExpired: (isReplayExpired: boolean) => void;

  inactiveReplay: boolean;
  onInactiveReplay: () => void;
  onRejoinReplay: () => void; // rejoin replay after inactive in 15 mins

  onZoomScaleChange?: (domains: [number, number]) => void;

  connectionId: string;
  setConnectionId: (connectionId: string) => void;
  shortIdRef: React.RefObject<string>;

  disableControlsHover: boolean;
  setDisableControlsHover: (disableControlsHover: boolean) => void;

  blocked: boolean;
  isInBackground: boolean;
  showingInVideoFormBeforeWatching: boolean;
  hasInVideoFormShowBeforeWatching: boolean;
  inTestMode: boolean;
  lastActiveDateRef: React.MutableRefObject<EDate>;
  markUserActive: () => void;

  locale?: Locale;
  interactionState: InteractionStateType;
  setInteractionState: React.Dispatch<React.SetStateAction<InteractionStateType>>;
  attendeeCount: number;
  setAttendeeCount: React.Dispatch<React.SetStateAction<number>>;

  agendaMenuShowed?: boolean;
  setAgendaMenuShowed: React.Dispatch<React.SetStateAction<boolean>>;

  enabledCC: boolean;
  setEnabledCC: (enabled: boolean) => void;
}

export type WebinarPlayerUIState = WebinarPlayerState & { animations: AnimationControls };

export const WebinarPlayerContext = React.createContext<WebinarPlayerUIState>(
  {} as unknown as WebinarPlayerUIState
);

export const useWebinarPlayerContext = (): WebinarPlayerUIState => {
  return React.useContext(WebinarPlayerContext);
};

export const withWebinarPlayerContext = createWithPartialContext(WebinarPlayerContext);
